import type { ItemItemValue } from '@/common/api/types/items';
import type { ItemTypeMap } from '@/common/api/types/itemType';
import { useItemFetchQueryWithPublished } from '@/features/item-panel/components/hooks/useItemFetchQueryWithPublished';
import { EmptyFieldValue } from '@/features/items/components/item-field-value/EmptyFieldValue';
import { Field } from '@/features/items/components/item-field-value/Field';
import { FieldWithStatusIndicator } from '@/features/items/components/item-field-value/FieldWithStatusIndicator';
import { ItemPicker } from './ItemPicker';
import { ItemValueButton } from './ItemValueButton';

export const ItemField = ({
  id,
  name,
  value,
  itemTypesById,
  isEditable = false,
  autoFocus = false,
  onChange,
  isPending,
  isSuccess,
  isError,
  error,
}: {
  id: string;
  name: string;
  value?: ItemItemValue;
  itemTypesById: ItemTypeMap;
  isEditable?: boolean;
  autoFocus?: boolean;
  onChange?: (itemId: number | null) => void;
  isPending?: boolean;
  isSuccess?: boolean;
  isError?: boolean;
  error?: Error | null;
}) => {
  const itemId = value?.ITEM;
  const { data: item } = useItemFetchQueryWithPublished({ itemId: itemId ?? 0, enabled: !!itemId });

  const inputId = `item-field-${id}`;

  const handleChange = (selectedItem: { id: number; name: string; itemTypeId: number } | null) => {
    const newItemId = selectedItem?.id ?? null;
    if (newItemId !== (itemId ?? null)) {
      onChange?.(newItemId);
    }
  };

  if (isEditable) {
    return (
      <FieldWithStatusIndicator
        inputId={inputId}
        name={name}
        isPending={isPending}
        isSuccess={isSuccess}
      >
        <div className="flex flex-col gap-1">
          <ItemPicker
            value={
              item
                ? {
                    id: item.id,
                    name: item.name,
                    itemTypeId: item.itemTypeId,
                  }
                : null
            }
            onChange={handleChange}
            itemTypesById={itemTypesById}
            autoFocus={autoFocus}
            disabled={isPending}
          />
          {isError && error && (
            <span className="text-xs text-text-error" data-testid="item-field-error">
              {error.message}
            </span>
          )}
        </div>
      </FieldWithStatusIndicator>
    );
  }

  return (
    <Field inputId={inputId} name={name}>
      <div className="text-sm text-text-neutral-primary" data-testid="item-field-value">
        {item ? <ItemValueButton item={item} itemTypesById={itemTypesById} /> : <EmptyFieldValue />}
      </div>
    </Field>
  );
};
